import { useMemo } from 'react'
import { Database, LanguageLevel } from '../types/database'
import { ExerciseInfo } from './StudyMode'

type Card = Database['public']['Tables']['cards']['Row']

interface StudyStartPageProps {
  cards: Card[]
  availableExercises: ExerciseInfo[]
  availableTags: string[]
  selectedExercises: string[]  // exercise IDs
  selectedTags: string[]
  onExerciseToggle: (exerciseId: string) => void
  onTagToggle: (tag: string) => void
  onStartStudy: () => void
  onStartSequential?: (tags: string[], exercise: string) => void
  levelSettings: { enabledLevels: LanguageLevel[]; filterMode: 'only' | 'prefer' }
}

export function StudyStartPage({
  cards,
  availableExercises,
  availableTags,
  selectedExercises,
  selectedTags,
  onExerciseToggle,
  onTagToggle,
  onStartStudy,
  onStartSequential,
  levelSettings,
}: StudyStartPageProps) {
  // Exercise card counts (by exercise_id)
  const exerciseCardCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const ex of availableExercises) {
      counts.set(ex.id, cards.filter((card) => card.exercise_id === ex.id).length)
    }
    return counts
  }, [availableExercises, cards])

  // Tag card counts
  const tagCardCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const tag of availableTags) {
      counts.set(tag, cards.filter((card) => card.tags.includes(tag)).length)
    }
    return counts
  }, [availableTags, cards])

  // Calculate matching cards count
  const matchingCardsCount = useMemo(() => {
    return cards.filter((card) => {
      const hasExerciseFilter = selectedExercises.length > 0
      const hasTagFilter = selectedTags.length > 0

      if (hasExerciseFilter || hasTagFilter) {
        const matchesExercise = hasExerciseFilter && card.exercise_id != null && selectedExercises.includes(card.exercise_id)
        const matchesTag = hasTagFilter && selectedTags.some((tag) => card.tags.includes(tag))
        if (!matchesExercise && !matchesTag) return false
      }

      if (levelSettings.filterMode === 'only' && card.level) {
        if (!levelSettings.enabledLevels.includes(card.level)) return false
      }
      return true
    }).length
  }, [cards, selectedExercises, selectedTags, levelSettings])

  // Check if anything is selected
  const hasSelection = selectedExercises.length > 0 || selectedTags.length > 0

  // Clear all selections
  const clearAll = () => {
    selectedExercises.forEach(onExerciseToggle)
    selectedTags.forEach(onTagToggle)
  }

  // Get selected exercise names for display
  const exerciseMap = useMemo(() => {
    return new Map(availableExercises.map((e) => [e.id, e.name]))
  }, [availableExercises])

  const hasContent = availableExercises.length > 0 || availableTags.length > 0

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">What do you want to study?</h2>

        {!hasContent ? (
          <p className="text-gray-500 text-center py-8">No cards available to study.</p>
        ) : (
          <>
            {/* Exercises */}
            {availableExercises.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Exercises</h3>
                <div className="flex flex-wrap gap-2">
                  {availableExercises.map((ex) => {
                    const count = exerciseCardCounts.get(ex.id) || 0
                    const selected = selectedExercises.includes(ex.id)
                    return (
                      <button
                        key={ex.id}
                        onClick={() => onExerciseToggle(ex.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-purple-600 text-white'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        {ex.name}
                        <span className={`ml-1.5 ${selected ? 'text-purple-200' : 'text-purple-400'}`}>
                          ({count})
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Tags */}
            {availableTags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => {
                    const count = tagCardCounts.get(tag) || 0
                    const selected = selectedTags.includes(tag)
                    return (
                      <button
                        key={tag}
                        onClick={() => onTagToggle(tag)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag.replace(/_/g, ' ')}
                        <span className={`ml-1.5 ${selected ? 'text-indigo-200' : 'text-gray-500'}`}>
                          ({count})
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Selection Summary */}
            {hasSelection && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Selected:</span>
                  <button onClick={clearAll} className="text-sm text-gray-500 hover:text-gray-700">
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedExercises.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                    >
                      {exerciseMap.get(id) || id}
                      <button
                        onClick={() => onExerciseToggle(id)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {selectedTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {tag.replace(/_/g, ' ')}
                      <button
                        onClick={() => onTagToggle(tag)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="text-sm text-gray-600 mb-6 py-3 border-t border-b border-gray-100">
              <div className="flex justify-between">
                <span>Available cards:</span>
                <span className="font-medium">{cards.length}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Matching selection:</span>
                <span className="font-medium">{matchingCardsCount}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onStartStudy}
                disabled={matchingCardsCount === 0}
                className="flex-1 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Studying
              </button>
              {onStartSequential && hasSelection && (
                <button
                  onClick={() => {
                    const tag = selectedTags[0] || selectedExercises[0] || ''
                    onStartSequential([...selectedTags, ...selectedExercises], tag)
                  }}
                  className="flex-1 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Start Sequential
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
